import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef } from 'react'
import { FlexStyle, Platform, StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native'
import { ProgressBar } from 'react-native-paper'

import { BottomSheetModal } from '@gorhom/bottom-sheet'
import { THREE_CARD_AMOUNT_OF_HANDS } from '@ppwr/poker'

import { LineProgress } from '../../components/LineProgress/LineProgress'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { GameModal, GameModalKinds, LayerType } from '../../models/game/game-modals'
import { ThreeCardHowToPlay } from './ThreeCardHowToPlay'
import { getRouteToLastVisitedTab } from '../../navigation'
import { useNavigationContainerRef, useRouter } from 'expo-router'

export const ThreeCardTop = observer(
  ({
    end,
    endText = '',
    style,
    goBack
  }: {
    end?: boolean
    endText?: string
    notAbsolute?: boolean
    style?: ViewStyle
    goBack?: boolean
  }) => {
    const router = useRouter()
    // const navigationState = useNavigationContainerRef().getState()
    const {
      analytics: { setEvent },
      game: {
        amountOfHands,
        leaveCurrentGame,
        pushGameModals,
        bots,
        isGameReadyToPlay,
        myPlayer,
        startGame,
        isPlayingGame,
        seatedPlayersWithSeatedObserver,
        isWalkthrough,
        isGamePlaying
      },
      session: { lastVisitedTab },
      network
    } = useStore()
    const bottomSheetModalRef = useRef<BottomSheetModal>(null)
    const isWebSocketConnected = network.isWebSocketConnected

    const goBackToGameOverScreen = () => {
      router.navigate('/game/three-card/game-over')
    }
    const handleGoBack = () => {
      // TODO: FIX this
      const wasShareOrGameOver = false // navigationState.routes.at(-1)?.name === GameRoutes.threeCardShareScreen
      if (
        wasShareOrGameOver
        // navigation.getState().routes.at(-1)?.name === GameRoutes.threeCardShareScreen ||
        // navigation.getState().routes.at(-1)?.name === GameRoutes.threeCardGameOverScreen
      ) {
        leaveCurrentGame(false)
        router.replace(getRouteToLastVisitedTab(lastVisitedTab))
      } else {
        pushGameModals(
          GameModal.create({
            kind: GameModalKinds.threeCardLeaveConfirm,
            props: {},
            layerType: LayerType.alert // noop
          })
        )
      }
    }

    const isReady = useMemo(() => {
      if ((isGameReadyToPlay || isPlayingGame) && myPlayer && bots.length === 2) {
        return true
      }
      return false
    }, [bots, myPlayer, isGameReadyToPlay, isPlayingGame])

    const handleLetsPlay = () => {
      startGame()
      setEvent(events.threeCard.started)
    }

    const hideOnEnd = { display: (end || goBack ? 'none' : 'flex') as FlexStyle['display'] }

    const showLoadingProgressBar =
      (!isWebSocketConnected || seatedPlayersWithSeatedObserver.length === 0) && !isWalkthrough

    useEffect(() => {
      const isGameReadyToStart = isGameReadyToPlay

      if (!isGameReadyToStart || isWalkthrough) return

      bottomSheetModalRef.current?.present()
    }, [isGameReadyToPlay, isWalkthrough])

    useEffect(() => {
      if (isGamePlaying) {
        bottomSheetModalRef.current?.close()
      }
    }, [isGamePlaying])

    return (
      <View style={[styles.container, style]}>
        <View style={styles.content}>
          <View style={[styles.side]}>
            <DS.Frame hidden={!goBack}>
              <TouchableOpacity onPress={goBackToGameOverScreen}>
                <DS.Icon name="Arrow---left" color="Global/Black" size={24} />
              </TouchableOpacity>
            </DS.Frame>
            <TouchableOpacity onPress={() => bottomSheetModalRef.current?.present()} style={hideOnEnd}>
              <DS.Icon name="Info" color="Global/Black" size={24} />
            </TouchableOpacity>
          </View>
          <DS.Text variant="Body Medium/Medium" style={[{ flex: 1, textAlign: 'center' }]}>
            {end ? endText : amountOfHands ? `Round ${amountOfHands} of ${THREE_CARD_AMOUNT_OF_HANDS}` : ''}
          </DS.Text>
          <TouchableOpacity onPress={handleGoBack} style={styles.side}>
            <DS.Icon name="Close" color="Global/Black" size={24} />
          </TouchableOpacity>
        </View>
        <View style={[styles.progressBarContainer, hideOnEnd]}>
          {showLoadingProgressBar ? (
            <LineProgress isLoading={showLoadingProgressBar} height={2} color={DS.palette['Brand/Maroon']} />
          ) : (
            <ProgressBar
              color={DS.palette['Brand/Maroon']}
              visible
              progress={amountOfHands / THREE_CARD_AMOUNT_OF_HANDS}
              style={{ height: 2 }}
            />
          )}
        </View>
        <ThreeCardHowToPlay
          ref={bottomSheetModalRef}
          isReady={isReady}
          isPlayingGame={isPlayingGame}
          handleLetsPlay={handleLetsPlay}
        />
      </View>
    )
  }
)

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignSelf: 'center',
    height: 64,
    width: '100%',
    marginBottom: Platform.OS === 'web' ? 64 : 0
  },
  content: {
    paddingHorizontal: 24,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  side: {
    width: 24,
    justifyContent: 'center',
    alignItems: 'center'
  },
  progressBarContainer: {
    height: 1,
    width: '100%',
    backgroundColor: 'transparent'
  }
})
